import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { ReducerEntityPrefixTypes, ReducerNamespaceTypes } from "enums";
import { type EmbeddedVideoFormFields } from "../Details/EmbeddedVideoForm";
import { type EmbeddedVideo } from "../services/types";
import { type EmbeddedVideoSource, type Settings } from "../types/models";

interface EmbeddedVideoDetails {
  id: number;
  title: string;
  description: string;
  videoUrl: string;
  source?: EmbeddedVideoSource;
  thumbnailUrl: string;
  isDraft: boolean;
  isPurchased: boolean;
  hasBeenPublished: boolean;
  dateModified?: string;
  publisherId: number;
  settings: Settings;
}

export interface EmbeddedVideoDetailsState {
  embeddedVideo: EmbeddedVideoDetails;
  isLoading: boolean;
  isLoaded: boolean;
  title: string;
}

const initialState: EmbeddedVideoDetailsState = {
  embeddedVideo: {
    id: -1,
    title: "",
    description: "",
    videoUrl: "",
    source: undefined,
    thumbnailUrl: "",
    isDraft: true,
    isPurchased: false,
    hasBeenPublished: false,
    dateModified: undefined,
    publisherId: -1,
    settings: {
      labels: [],
      softwareApplications: [],
      flows: [],
      packs: [],
    },
  },
  title: "",
  isLoaded: false,
  isLoading: false,
};

const embeddedVideoDetailsSlice = createSlice({
  name: `${ReducerNamespaceTypes.Library}/${ReducerEntityPrefixTypes.EmbeddedVideos}/details`,
  initialState,
  reducers: {
    reset: () => initialState,
    save(state: EmbeddedVideoDetailsState, action: PayloadAction<EmbeddedVideoFormFields>) {
      state.embeddedVideo.title = action.payload.title;
      state.embeddedVideo.description = action.payload.description;
      state.embeddedVideo.videoUrl = action.payload.videoUrl;
      state.embeddedVideo.source = action.payload.source;
      state.embeddedVideo.thumbnailUrl = action.payload.thumbnailUrl;
    },
    saveSettings(
      state: EmbeddedVideoDetailsState,
      action: PayloadAction<{ labels: string[]; softwareApplications: string[] }>,
    ) {
      state.embeddedVideo.settings.labels = action.payload.labels;
      state.embeddedVideo.settings.softwareApplications = action.payload.softwareApplications;
    },
    fetchBegin(state: EmbeddedVideoDetailsState) {
      state.isLoading = true;
    },
    fetchSuccess(state: EmbeddedVideoDetailsState, action: PayloadAction<EmbeddedVideo>) {
      const { videoSourceId, packs, flows, labels, softwareApplications, ...rest } = action.payload;

      state.isLoaded = true;
      state.isLoading = false;
      state.title = rest.title;
      state.embeddedVideo = {
        ...rest,
        source: videoSourceId,
        settings: {
          labels,
          softwareApplications,
          flows,
          packs: packs.map((pack) => ({ title: pack.name, id: pack.id })),
        },
      };
    },
    fetchFailure(state: EmbeddedVideoDetailsState) {
      state.isLoading = false;
    },
    setLoaded(state: EmbeddedVideoDetailsState) {
      state.isLoaded = true;
    },
  },
});

export const { save, saveSettings, fetchBegin, fetchSuccess, fetchFailure, reset, setLoaded } =
  embeddedVideoDetailsSlice.actions;

export default embeddedVideoDetailsSlice.reducer;
